import { Badge, Button, Icon } from '@ds/core';
import { Alert, Modal } from '@ds/feedback';
import { dataSimples, moeda } from '@/comuns/formato';
import type { Orcamento, StatusDoOrcamento } from '@/data/tipos';

export const STATUS_DO_ORCAMENTO: Record<StatusDoOrcamento, { rotulo: string; tom: 'success' | 'info' | 'danger' | 'neutral' | 'warning' }> = {
    rascunho: { rotulo: 'Rascunho', tom: 'neutral' },
    enviado: { rotulo: 'Enviado', tom: 'info' },
    aprovado: { rotulo: 'Aprovado', tom: 'success' },
    recusado: { rotulo: 'Recusado', tom: 'danger' },
    expirado: { rotulo: 'Expirado', tom: 'warning' },
};

/** Aprovado já gerou pacote; recusado e expirado são história. Esses se duplicam. */
export function orcamentoEditavel(o: Orcamento): boolean {
    return o.status === 'rascunho' || o.status === 'enviado';
}

export interface DetalheDoOrcamentoModalProps {
    orcamento: Orcamento | null;
    /** O orçamento da URL que não abriu — some da clínica, ou nunca foi dela. */
    erro: string | null;
    onFechar: () => void;
    onEditar: (o: Orcamento) => void;
    onDuplicar: (o: Orcamento) => void;
    onGerarPdf: (o: Orcamento) => void;
    onDecidir: (o: Orcamento, status: StatusDoOrcamento) => void;
}

/**
 * Detalhe do orçamento — era drawer no protótipo, virou modal.
 *
 * Mostra os ITENS gravados, com o total de cada um. O protótipo repetia o total
 * da proposta em cada linha: quem conferia via o mesmo número três vezes.
 */
export function DetalheDoOrcamentoModal({ orcamento: o, erro, onFechar, onEditar, onDuplicar, onGerarPdf, onDecidir }: DetalheDoOrcamentoModalProps) {
    return (
        <Modal
            open width={620} onClose={onFechar}
            title={o ? o.numero : 'Orçamento'}
            description={o ? `${o.paciente} · ${moeda(o.totalCentavos)}` : undefined}
            footer={o ? (
                <>
                    <Button variant="ghost" iconLeft={<Icon name="file-down" size={15} />} onClick={() => onGerarPdf(o)}>Gerar PDF</Button>
                    <Button variant="ghost" iconLeft={<Icon name="copy" size={15} />} onClick={() => onDuplicar(o)}>Duplicar</Button>
                    <span style={{ marginLeft: 'auto' }} />
                    {orcamentoEditavel(o) && (
                        <Button variant="secondary" iconLeft={<Icon name="pencil" size={15} />} onClick={() => onEditar(o)}>Editar proposta</Button>
                    )}
                    {o.status === 'rascunho' && (
                        <Button iconLeft={<Icon name="send" size={15} />} onClick={() => onDecidir(o, 'enviado')}>Marcar como enviado</Button>
                    )}
                    {o.status === 'enviado' && (
                        <>
                            <Button variant="secondary" onClick={() => onDecidir(o, 'recusado')}>Recusar</Button>
                            <Button iconLeft={<Icon name="circle-check" size={15} />} onClick={() => onDecidir(o, 'aprovado')}>Aprovar</Button>
                        </>
                    )}
                </>
            ) : <Button variant="secondary" onClick={onFechar}>Fechar</Button>}
        >
            {!o ? (
                <Alert tone="danger" title="Não foi possível abrir o orçamento" dismissible={false}>
                    {erro ?? 'Orçamento não encontrado.'}
                </Alert>
            ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
                        <Badge tone={STATUS_DO_ORCAMENTO[o.status].tom} dot>{STATUS_DO_ORCAMENTO[o.status].rotulo}</Badge>
                        <span style={{ fontSize: 12.5, color: 'var(--text-secondary)' }}>
                            Criado em {dataSimples(o.criadoEm)}{o.validade ? ` · válido até ${dataSimples(o.validade)}` : ''} · {o.responsavel}
                        </span>
                    </div>

                    <div style={{ overflowX: 'auto', border: '1px solid var(--border-subtle)', borderRadius: 'var(--radius-md)' }}>
                        <table style={{ width: '100%', borderCollapse: 'collapse', fontFamily: 'var(--font-ui)', fontSize: 13 }}>
                            <thead>
                                <tr style={{ background: 'var(--surface-secondary)' }}>
                                    {['Item', 'Qtd', 'Valor unit.', 'Desconto', 'Total'].map((c, n) => (
                                        <th key={c} scope="col" style={{ padding: '9px 12px', textAlign: n === 0 ? 'left' : 'right', fontSize: 12, fontWeight: 600, color: 'var(--text-secondary)', whiteSpace: 'nowrap' }}>{c}</th>
                                    ))}
                                </tr>
                            </thead>
                            <tbody>
                                {o.itens.map((i) => (
                                    <tr key={i.id} style={{ borderTop: '1px solid var(--border-subtle)' }}>
                                        <td style={{ padding: '9px 12px', color: 'var(--text-body)' }}>{i.descricao}</td>
                                        <td style={{ padding: '9px 12px', textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>{i.quantidade}</td>
                                        <td style={{ padding: '9px 12px', textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>{moeda(i.valorUnitarioCentavos)}</td>
                                        <td style={{ padding: '9px 12px', textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>{i.descontoCentavos ? moeda(i.descontoCentavos) : '—'}</td>
                                        <td style={{ padding: '9px 12px', textAlign: 'right', fontWeight: 600, fontVariantNumeric: 'tabular-nums' }}>
                                            {moeda(Math.max(0, i.quantidade * i.valorUnitarioCentavos - i.descontoCentavos))}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                            <tfoot>
                                <tr style={{ borderTop: '1px solid var(--border)' }}>
                                    <th scope="row" colSpan={4} style={{ padding: '10px 12px', textAlign: 'left', fontWeight: 600 }}>Total</th>
                                    <td style={{ padding: '10px 12px', textAlign: 'right', fontWeight: 700, fontVariantNumeric: 'tabular-nums' }}>{moeda(o.totalCentavos)}</td>
                                </tr>
                            </tfoot>
                        </table>
                    </div>

                    {o.status === 'aprovado' && (
                        <p style={{ margin: 0, fontSize: 12.5, color: 'var(--text-secondary)' }}>
                            Aprovado: cada item com procedimento e mais de uma sessão virou pacote. Para mudar a proposta, duplique.
                        </p>
                    )}
                    {(o.status === 'recusado' || o.status === 'expirado') && (
                        <p style={{ margin: 0, fontSize: 12.5, color: 'var(--text-secondary)' }}>
                            Proposta {STATUS_DO_ORCAMENTO[o.status].rotulo.toLowerCase()} não se edita. Duplique para fazer outra.
                        </p>
                    )}
                </div>
            )}
        </Modal>
    );
}
